import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Divider,
  Chip,
  CircularProgress,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useSelector } from "react-redux";
import axios from "axios";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";
import { showToast, ACCREDITEDKYCWEBSERVICE } from "../../CoreDataService";

// Maturity / funding status vocabulary — see BRS "8. Principal Payable Wallet
// — By Security" and "10. Maturity Funding Requirement".
const STATUS = {
  UPCOMING: "Upcoming",
  DUE: "Due",
  PROCESSING: "Payment Processing",
  PAID: "Paid",
  OVERDUE: "Overdue",
};

const statusColor = (status) => {
  switch (status) {
    case STATUS.PAID:
      return "#16a34a";
    case STATUS.DUE:
    case STATUS.PROCESSING:
      return "#f59e0b";
    case STATUS.OVERDUE:
      return "#ef4444";
    default:
      return "#64748b";
  }
};

const IssuerPrincipalPayableWallet = () => {
  const { brokerBasicInfo, btncolor } = useSelector((state) => state);

  // issuerId / uuid identify the logged-in Issuer for every accreditation
  // Security-Interest call — stored at login the same way as elsewhere in
  // the app (e.g. MyWalletSpot.js, IssuerInterestPayableWallet.js).
  const issuerId = localStorage.getItem("userId");
  const uuid = localStorage.getItem("uuid");
  const accessToken = localStorage.getItem("access_token");
  // The wallet always operates in the Issuer's Home Currency (BRS §16), so the
  // transfer call uses the account's homeCurrencyId rather than anything the
  // wallet-balance response returns.
  const homeCurrencyId = localStorage.getItem("homeCurrencyId");

  const [loading, setLoading] = useState(true);
  const [byAsset, setByAsset] = useState([]);
  const [paymentHistory] = useState([]);

  const [walletCurrencyCode, setWalletCurrencyCode] = useState(
    localStorage.getItem("homeCurrency") || "CAD"
  );
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalPrincipalLiability, setTotalPrincipalLiability] = useState(0);
  const [totalDueOverdue, setTotalDueOverdue] = useState(0);

  const [transferModal, setTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  const currencySymbol = localStorage.getItem("homeCurrencySymbol") || "$";

  useEffect(() => {
    getPrincipalWalletBalance();
  }, []);

  const getPrincipalWalletBalance = () => {
    setLoading(true);
    axios
      .get(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/get-principal-wallet-balance`,
        {
          params: { issuerId, uuid },
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + accessToken,
          },
        }
      )
      .then((response) => {
        const res = response?.data;
        if (!res?.success) {
          showToast("error", res?.message || "Could not load Principal Payable Wallet details");
          return;
        }
        const data = res.data || {};
        const wallet = (data.walletDetails || [])[0];

        setWalletCurrencyCode(wallet?.currencyCode || walletCurrencyCode);
        setWalletBalance(wallet?.currentBalance || 0);
        setTotalPrincipalLiability(data.totalPrincipalLiability || 0);
        setTotalDueOverdue(data.totalDueOverdue || 0);

        setByAsset(
          (data.securityDetails || []).map((s) => ({
            securityId: s.securityId,
            assetId: s.assetId,
            asset: s.asset,
            outstandingPrincipal: s.outstandingPrincipal || 0,
            maturityDate: s.maturityDate
              ? new Date(s.maturityDate).toLocaleDateString()
              : "—",
            principalDue: s.principalDue || 0,
            status:
              s.status === "UPCOMING"
                ? STATUS.UPCOMING
                : s.status === "DUE"
                ? STATUS.DUE
                : s.status === "PROCESSING"
                ? STATUS.PROCESSING
                : s.status === "PAID"
                ? STATUS.PAID
                : s.status === "OVERDUE"
                ? STATUS.OVERDUE
                : s.status,
          }))
        );
      })
      .catch((reason) => {
        if (reason?.response?.data?.error === "invalid_token") {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", "Could not connect to server");
        }
      })
      .finally(() => setLoading(false));
  };

  const fmt = (n) =>
    `${currencySymbol}${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleCloseTransferModal = () => (
    setTransferModal(false), setTransferAmount("")
  );

  const onlyAmount = (e) => {
    setTransferAmount(e.target.value.replace(/[^0-9.]/g, ""));
  };

  const submitTransfer = () => {
    const amount = parseFloat(transferAmount);
    if (!amount || amount <= 0) {
      showToast("error", "Enter a valid amount to transfer");
      return;
    }
    if (!homeCurrencyId) {
      showToast("error", "Home Currency could not be determined. Please try again.");
      return;
    }

    setTransferSubmitting(true);
    // Moves funds Spot Wallet → Principal Payable Wallet, both in the Issuer's
    // Home Currency (BRS §9). No separate deposit mechanism is created.
    // transferToSecurityWallet: 1 = Spot Wallet -> Principal Wallet (this action).
    axios
      .post(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/transfer-to-principal-wallet`,
        {
          uuid,
          issuerId,
          currencyId: homeCurrencyId,
          amount,
          transferToSecurityWallet: 1,
        },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + accessToken,
          },
        }
      )
      .then((response) => {
        const res = response?.data;
        if (!res?.success) {
          showToast("error", res?.message || "Transfer failed");
          return;
        }
        showToast("success", res.message || "Amount transferred successfully to Principal Wallet.");
        handleCloseTransferModal();
        getPrincipalWalletBalance();
      })
      .catch((reason) => {
        if (reason?.response?.data?.error === "invalid_token") {
          showToast("error", "SESSION TIMED OUT. LOGIN AGAIN");
        } else {
          showToast("error", reason?.response?.data?.message || "Could not connect to server");
        }
      })
      .finally(() => setTransferSubmitting(false));
  };

  const overallStatus = totalDueOverdue > 0 ? STATUS.OVERDUE : STATUS.UPCOMING;

  const statsMeta = [
    {
      label: "Wallet Balance",
      value: fmt(walletBalance),
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 20 }} />,
      color: btncolor,
    },
    {
      label: "Total Principal Liability",
      value: fmt(totalPrincipalLiability),
      icon: <PaymentsOutlinedIcon sx={{ fontSize: 20 }} />,
      color: "#16a34a",
    },
    {
      label: "Total Due / Overdue",
      value: fmt(totalDueOverdue),
      icon: <ReportProblemOutlinedIcon sx={{ fontSize: 20 }} />,
      color: totalDueOverdue > 0 ? "#ef4444" : "#64748b",
    },
  ];

  return (
    <Box sx={pageWrapper}>
      {/* Hero */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.72)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <AccountBalanceOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>
              Principal Payable Wallet
              <Typography component="span" sx={heroCurrencyTag}>
                {walletCurrencyCode}
              </Typography>
            </Typography>
            <Typography sx={heroSubtitle}>
              Fund and track principal repayment obligations for maturing
              Securities issued by {brokerBasicInfo?.companyName || "your"}{" "}
              account.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Overdue banner — driven by totalDueOverdue (BRS §21 "Overdue Principal") */}
      {overallStatus === STATUS.OVERDUE && (
        <Box
          sx={{
            ...alertBanner,
            borderColor: alpha(statusColor(overallStatus), 0.4),
            backgroundColor: alpha(statusColor(overallStatus), 0.08),
          }}
        >
          <ReportProblemOutlinedIcon sx={{ color: statusColor(overallStatus) }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ ...alertTitle, color: statusColor(overallStatus) }}>
              Principal Payment Overdue
            </Typography>
            <Typography sx={alertBody}>
              Outstanding principal was not fully funded by its maturity date.
              Fund the Principal Payable Wallet to clear the shortfall. The
              system does not automatically cancel, retire, extend, convert
              or sell the affected Security.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Stats */}
      <Box sx={statsStrip}>
        {statsMeta.map((s, i) => (
          <Box key={i} sx={statCard}>
            <Box
              sx={{
                ...statIconWrap,
                backgroundColor: alpha(s.color || "#1976d2", 0.1),
                color: s.color,
              }}
            >
              {s.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={statLabel}>{s.label}</Typography>
            </Box>
            <Box sx={statValueWrap}>
              <Typography sx={{ ...statValue, color: s.color }}>
                {s.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <ContainedButton
          onClick={() => setTransferModal(true)}
          sx={{ height: 40, px: 2.5, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
        >
          <SwapHorizOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />
          Transfer from Spot Wallet
        </ContainedButton>
      </Box>

      {/* By Security */}
      <Box sx={{ ...mainCard, mb: 3 }}>
        <Box sx={sectionHeaderWrap}>
          <Typography sx={sectionHeaderTitle}>By Security</Typography>
        </Box>
        <TableContainer component={Paper} sx={modernTableContainer}>
          <Table sx={tableStyle}>
            <TableHead>
              <TableRow sx={modernTableHeadRow}>
                {[
                  "Asset",
                  "Outstanding Principal",
                  "Maturity Date",
                  "Principal Due",
                  "Status",
                ].map((col) => (
                  <TableCell key={col} sx={modernTableHeadCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={emptyCell}>
                    <CircularProgress size={28} sx={{ color: "var(--text-color)", opacity: 0.6 }} />
                  </TableCell>
                </TableRow>
              ) : byAsset.length > 0 ? (
                byAsset.map((row, i) => (
                  <TableRow key={row.securityId ?? i} sx={modernTableBodyRow}>
                    <TableCell sx={{ ...modernTableBodyCell, fontWeight: 700 }}>
                      {row.asset}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {fmt(row.outstandingPrincipal)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.maturityDate}</TableCell>
                    <TableCell
                      sx={{
                        ...modernTableBodyCell,
                        color: row.principalDue > 0 ? "#ef4444" : "var(--text-color)",
                        fontWeight: row.principalDue > 0 ? 700 : 400,
                      }}
                    >
                      {fmt(row.principalDue)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          backgroundColor: alpha(statusColor(row.status), 0.12),
                          color: statusColor(row.status),
                          fontWeight: 600,
                          fontSize: "0.72rem",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={emptyCell}>
                    <InboxOutlinedIcon sx={emptyIcon} />
                    <Typography sx={emptyText}>
                      No Securities are currently configured for principal
                      repayment.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Principal Payment History — BRS §23 */}
      <Box sx={{ ...mainCard, mt: 3 }}>
        <Box sx={sectionHeaderWrap}>
          <Typography sx={sectionHeaderTitle}>Principal Payment History</Typography>
        </Box>
        <TableContainer component={Paper} sx={modernTableContainer}>
          <Table sx={tableStyle}>
            <TableHead>
              <TableRow sx={modernTableHeadRow}>
                {[
                  "Asset",
                  "Maturity",
                  "Quantity",
                  "Principal Due",
                  "Principal Paid",
                  "Status",
                  "Payment Date",
                ].map((col) => (
                  <TableCell key={col} sx={modernTableHeadCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentHistory.length > 0 ? (
                paymentHistory.map((row, i) => (
                  <TableRow key={row.id ?? i} sx={modernTableBodyRow}>
                    <TableCell sx={{ ...modernTableBodyCell, fontWeight: 700 }}>
                      {row.asset}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.maturityDate}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.quantity}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{fmt(row.principalDue)}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{fmt(row.principalPaid)}</TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          backgroundColor: alpha(statusColor(row.status), 0.12),
                          color: statusColor(row.status),
                          fontWeight: 600,
                          fontSize: "0.72rem",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.paymentDate}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={emptyCell}>
                    <InboxOutlinedIcon sx={emptyIcon} />
                    <Typography sx={emptyText}>
                      No principal payment cycles have been generated yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Transfer from Spot Wallet */}
      <Dialog
        open={transferModal}
        onClose={handleCloseTransferModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          Transfer from Spot Wallet
          <IconButton onClick={handleCloseTransferModal} size="small" sx={dialogCloseBtn}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogContent sx={dialogContentStyle}>
          <Box sx={redeemInfoGrid}>
            <Box sx={redeemInfoRow}>
              <Typography sx={redeemInfoLabel}>Principal Payable Wallet Balance</Typography>
              <Typography sx={redeemInfoValue}>{fmt(walletBalance)}</Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={fieldLabelSmall}>
              Amount ({walletCurrencyCode})
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter amount"
              value={transferAmount}
              onChange={onlyAmount}
              sx={dialogTextField}
            />
          </Box>
          <Typography sx={{ ...dialogText, mt: 2, opacity: 0.7 }}>
            * Funds are moved from your Spot Wallet ({walletCurrencyCode}) into
            this Principal Payable Wallet. This balance is segregated from the
            Interest Payable Wallet balance.
          </Typography>
        </DialogContent>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions sx={dialogActionsStyle}>
          <ContainedButton
            fullWidth
            onClick={submitTransfer}
            disabled={transferSubmitting}
            sx={{ mx: 1 }}
          >
            {transferSubmitting ? "Submitting..." : "Submit"}
          </ContainedButton>
          <OutlinedButton onClick={handleCloseTransferModal} sx={{ mx: 1 }}>
            Cancel
          </OutlinedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Styles (shared visual language with the Interest Payable Wallet page) ──

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const heroCurrencyTag = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.22)",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: "6px",
  px: 1,
  py: 0.25,
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 620,
};

const alertBanner = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  border: "1px solid",
  borderRadius: "14px",
  p: 2,
  mb: 3,
};

const alertTitle = {
  fontSize: "0.92rem",
  fontWeight: 700,
  mb: 0.25,
};

const alertBody = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.75,
  lineHeight: 1.6,
};

const statsStrip = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
  gap: 2,
  mb: 3,
};

const statCard = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 2,
  backgroundColor: "var(--body-color-less)",
  border: "1px solid rgba(125,125,125,0.15)",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
  },
};

const statIconWrap = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const statLabel = {
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const statValueWrap = {
  textAlign: "right",
  flexShrink: 0,
};

const statValue = {
  fontSize: "1.2rem",
  fontWeight: 700,
  lineHeight: 1,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const sectionHeaderWrap = {
  px: { xs: 2, md: 3 },
  py: 2,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  backgroundColor: "var(--body-color-less2)",
};

const sectionHeaderTitle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
};

const modernTableContainer = {
  borderRadius: 0,
  border: "none",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 760,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
  maxWidth: 400,
  mx: "auto",
  lineHeight: 1.6,
};

const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 440 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.05rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  pt: 3,
  pb: 2,
  px: { xs: 2.5, md: 3 },
  backgroundColor: "var(--popupbody-color)",
};

const dialogText = {
  fontSize: "0.88rem",
  color: "var(--text-color)",
  opacity: 0.85,
  lineHeight: 1.65,
};

const dialogActionsStyle = {
  justifyContent: "center",
  px: 3,
  pb: 2.5,
  backgroundColor: "var(--popupbody-color)",
};

const redeemInfoGrid = {
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  overflow: "hidden",
};

const redeemInfoRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
  py: 1.5,
};

const redeemInfoLabel = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

const redeemInfoValue = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "var(--text-color)",
};

const fieldLabelSmall = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.6,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  mb: 0.75,
};

const dialogTextField = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.45)",
  },
};

export default IssuerPrincipalPayableWallet;
